type PlayResult = {
    source: AudioBufferSourceNode;
    buffer: AudioBuffer;
};

export type PlayOptions = {
    sampleRate?: number; // default 44100
    channels?: number; // default 1 (mono)
    bitDepth?: number; // 16 = signed int16, 8 = unsigned uint8, 32 = float32
    float?: boolean; // if true and bitDepth===32, interpret as float32
    littleEndian?: boolean; // default true
};

export function createAudioContext(): AudioContext {
    const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext) as {
        new (): AudioContext;
    };
    return new AudioCtx();
}

/**
 * Play PCM base64 data on context. Defaults: 44100 Hz, 1 channel, 16-bit little-endian signed PCM.
 */
export async function playPcmBase64(context: AudioContext, base64: string, opts?: PlayOptions): Promise<PlayResult> {
    const {
        sampleRate = 44100,
        channels = 1,
        bitDepth = 16,
        float = bitDepth === 32,
        littleEndian = true,
    } = opts || {};

    function base64ToArrayBuffer(b64: string): ArrayBuffer {
        const binary = atob(b64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        return bytes.buffer;
    }

    const ab = base64ToArrayBuffer(base64);
    const dv = new DataView(ab);
    const bytesPerSample = bitDepth / 8;
    const totalSamples = Math.floor(dv.byteLength / bytesPerSample);
    const frames = Math.floor(totalSamples / channels);

    // Prepare per-channel Float32 arrays
    const channelData: Float32Array[] = new Array(channels);
    for (let c = 0; c < channels; c++) channelData[c] = new Float32Array(frames);

    // Convert samples (assume interleaved channels)
    for (let frame = 0; frame < frames; frame++) {
        for (let ch = 0; ch < channels; ch++) {
            const sampleIndex = frame * channels + ch;
            const byteOffset = sampleIndex * bytesPerSample;
            let sample = 0;

            if (bitDepth === 16 && !float) {
                sample = dv.getInt16(byteOffset, littleEndian) / 32768; // normalize to -1..1
            } else if (bitDepth === 8 && !float) {
                // 8-bit PCM often unsigned 0..255 -> convert to -1..1
                sample = (dv.getUint8(byteOffset) - 128) / 128;
            } else if (bitDepth === 32 && float) {
                sample = dv.getFloat32(byteOffset, littleEndian); // already -1..1
            } else if (bitDepth === 32 && !float) {
                // signed int32 -> normalize
                sample = dv.getInt32(byteOffset, littleEndian) / 2147483648;
            } else {
                // fallback: read as 16-bit signed
                sample = dv.getInt16(byteOffset, littleEndian) / 32768;
            }

            // clamp just in case
            if (sample > 1) sample = 1;
            if (sample < -1) sample = -1;
            channelData[ch][frame] = sample;
        }
    }

    // Resume AudioContext
    if (context.state === 'suspended') {
        try {
            await context.resume();
        } catch {
            // may require user gesture in some browsers
        }
    }

    const buffer = context.createBuffer(channels, frames, sampleRate);
    for (let c = 0; c < channels; c++) {
        buffer.copyToChannel(channelData[c], c, 0);
    }

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.start();

    return { source, buffer };
}
